import { useEffect, useState } from "react";
import * as T from "../../../utilities/frontendTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/ui/select";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Checkbox } from "@components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Label } from "@components/ui/label";
import textData from "../../../textData.json";
import { AlertCircle } from "lucide-react";

interface NeedHelpWithSectionProps {
  founderProfile: T.StartupFounderProfile | null;
  regionalFirmOptions: {value: string, text: string, frText: string}[];
  onSave: (needHelpWith: T.NeedHelpWith) => void;
  onValidationFail: () => void;
  langState: T.LangState;
  onSaveRef?: React.MutableRefObject<(() => void) | null>;
}

const topicsOptions = [
  { value: "Patents", text: "Patents", frText: "Brevets" },
  { value: "Industrial Designs/ Design Patents", text: "Industrial Designs/ Design Patents", frText: "Designs industriels/brevets de conception" },
  { value: "Trademarks", text: "Trademarks", frText: "Marques de commerce" },
  { value: "Trade Secrets", text: "Trade Secrets", frText: "Secrets commerciaux" },
  { value: "Copyright", text: "Copyright", frText: "Droits d'auteur" },
  { value: "Open Source Asset Use", text: "Open Source Asset Use", frText: "Utilisation des ressources « open source »" },
  { value: "IP Strategy", text: "IP Strategy", frText: "Stratégie de PI" },
  { value: "IP Ownership", text: "IP Ownership", frText: "Propriété de la PI" },
  { value: "Freedom to Operate", text: "Freedom to Operate", frText: "Liberté d'exploitation" },
  { value: "Review Prior Art", text: "Review Prior Art", frText: "Examen de l'art antérieur" }
]

const desiredActivitiesOptions = [
  { value: "IP Strategy", text: "IP Strategy", frText: "Stratégie de PI" },
  { value: "Invention/Patent Mining", text: "Invention/Patent Mining", frText: "Exploration d'inventions/brevets" },
  { value: "Patentability/Novelty/Prior Art Search", text: "Patentability/Novelty/Prior Art Search", frText: "Recherche de brevetabilité/nouveauté/art antérieur" },
  { value: "Patent Landscape Report", text: "Patent Landscape Report", frText: "Rapport sur le paysage des brevets" },
  { value: "Trademark Clearance Search", text: "Trademark Clearance Search", frText: "Recherche de disponibilité de marque" },
  { value: "Trade Secret Protection Plan", text: "Trade Secret Protection Plan", frText: "Plan de protection des secrets commerciaux" },
  { value: "Patent Filing", text: "Patent Filing", frText: "Dépôt de brevet" },
  { value: "Patent Conversion", text: "Patent Conversion", frText: "Conversion de brevet" },
  { value: "Patent Prosecution", text: "Patent Prosecution", frText: "Poursuite de brevet" },
  { value: "Trademark Filing", text: "Trademark Filing", frText: "Dépôt de marque" },
  { value: "Licensing Agreement Review/Drafting", text: "Licensing Agreement Review/Drafting", frText: "Révision/rédaction d'accord de licence" },
  { value: "Design Patent/Industrial Design Application", text: "Design Patent/Industrial Design Application", frText: "Demande de brevet de design/dessin industriel" },
  { value: "Other", text: "Other", frText: "Autre" }
]

const patentsReadyOptionsOptions = [
  {
    value: "My IP service provider has conducted a favorable patentability/prior art/novelty search for the technology I plan to patent.",
    text: "My IP service provider has conducted a favorable patentability/prior art/novelty search for the technology I plan to patent.",
    frText: "Mon fournisseur de services en PI a effectué une recherche favorable de brevetabilité/art antérieur/nouveauté pour la technologie que je prévois breveter.",
  },
  {
    value: "I have a granted patent in the same patent family.",
    text: "I have a granted patent in the same patent family.",
    frText: "J'ai un brevet accordé dans la même famille de brevets.",
  },
  {
    value: "I have received a favorable written opinion from my PCT application.",
    text: "I have received a favorable written opinion from my PCT application.",
    frText: "J'ai reçu un avis écrit favorable de ma demande PCT.",
  },
]

const requiredFieldErrorMessage = (langState: T.LangState) => (
  <p className="flex items-center gap-1 ml-2 px-2 font-semibold text-danger text-sm">
    <AlertCircle className="size-4" />{langState === "fr" ? "Champ obligatoire" : "Required Field"} {/* TODO-FR-REVIEW */}
  </p>
);

function NeedHelpWithSection({ founderProfile, regionalFirmOptions, onSave, onValidationFail, langState, onSaveRef }: NeedHelpWithSectionProps) {
  const [seekingEducation, setSeekingEducation] = useState<boolean | null>(null)
  const [businessGoal, setBusinessGoal] = useState<boolean | null>(null)
  const [agreementSigned, setAgreementSigned] = useState<string | null>(null)
  const [workingWithFirm, setWorkingWithFirm] = useState<boolean | null>(null)
  const [govtSupport, setGovtSupport] = useState<boolean | null>(null)
  const [topics, setTopics] = useState<string[]>([]);
  const [desiredActivities, setDesiredActivities] = useState<string[]>([]);
  const [patentsReadyOptions, setPatentsReadyOptions] = useState<string[]>([]);
  const [needHelpWithStateObj, setNeedHelpWithStateObj] = useState<{ [key: string]: any }>({});
  const [requireDesiredActivitesOther, setRequireDesiredActivitesOther] = useState(false)
  const [requireFirmOther, setRequireFirmOther] = useState(false)
  const [requireSupportOrgOther, setRequireSupportOrgOther] = useState(false)
  const [errorArray, setErrorArray] = useState<string[]>([]);
  const [submittedSectionError, setSubmittedSectionError] = useState(false)

  // Initialize state from founderProfile
  useEffect(() => {
    const initialProfile = founderProfile;
    if (!initialProfile?.needHelpWith) return;

    setSeekingEducation(initialProfile.needHelpWith.seekingEducation)
    setBusinessGoal(initialProfile.needHelpWith.businessGoal)
    setTopics(initialProfile.needHelpWith.topics)
    setDesiredActivities(initialProfile.needHelpWith.desiredActivities)
    setPatentsReadyOptions(initialProfile.needHelpWith.patentsReadyOptions || [])
    // Backwards compat: old boolean agreementSigned → string
    const storedAgreement = initialProfile.needHelpWith.agreementSigned;
    if (typeof storedAgreement === "boolean") {
      setAgreementSigned(storedAgreement ? "yes" : "no");
    } else if (typeof storedAgreement === "string") {
      setAgreementSigned(storedAgreement);
    }
    if (initialProfile?.needHelpWith.specificDiscussion) {
      setNeedHelpWithStateObj(prev => ({...prev, specificDiscussion: initialProfile?.needHelpWith?.specificDiscussion}))
    }
    if (initialProfile?.needHelpWith.desiredActivities.includes("Other") && initialProfile?.needHelpWith.activitiesOther) {
      setNeedHelpWithStateObj(prev => ({...prev, activitiesOther: initialProfile?.needHelpWith?.activitiesOther}))
      setRequireDesiredActivitesOther(true)
    }
    if (initialProfile?.needHelpWith.searchCompleted) {
      setNeedHelpWithStateObj(prev => ({...prev, searchCompleted: initialProfile?.needHelpWith?.searchCompleted}))
    }
    if (initialProfile?.needHelpWith.firmId) {
      setNeedHelpWithStateObj(prev => ({...prev, firmId: initialProfile?.needHelpWith?.firmId}))
    }
    if (initialProfile?.needHelpWith.firmId === "Other" && initialProfile?.needHelpWith.firmOther) {
      setNeedHelpWithStateObj(prev => ({...prev, firmOther: initialProfile?.needHelpWith?.firmOther}))
      setRequireFirmOther(true)
    }
    if (initialProfile?.needHelpWith.exclusiveRights) {
      setNeedHelpWithStateObj(prev => ({...prev, exclusiveRights: initialProfile?.needHelpWith?.exclusiveRights}))
    }
    if (initialProfile?.needHelpWith.ipOwnerName) {
      setNeedHelpWithStateObj(prev => ({...prev, ipOwnerName: initialProfile?.needHelpWith?.ipOwnerName}))
    }
    if (initialProfile?.needHelpWith.govtSupport !== null && initialProfile?.needHelpWith.govtSupport !== undefined) {
      setNeedHelpWithStateObj(prev => ({...prev, govtSupport: initialProfile?.needHelpWith?.govtSupport}))
      setGovtSupport(initialProfile.needHelpWith.govtSupport)
    }
    if (initialProfile?.needHelpWith.supportOrg) {
      setNeedHelpWithStateObj(prev => ({...prev, supportOrg: initialProfile?.needHelpWith?.supportOrg}))
    }
    if (initialProfile?.needHelpWith.supportOrgOther) {
      setNeedHelpWithStateObj(prev => ({...prev, supportOrgOther: initialProfile?.needHelpWith?.supportOrgOther}))
    }
    if (initialProfile?.needHelpWith.workingWithFirm || initialProfile?.needHelpWith.workingWithFirm === false) {
      setNeedHelpWithStateObj(prev => ({...prev, workingWithFirm: initialProfile?.needHelpWith?.workingWithFirm}))
      setWorkingWithFirm(initialProfile.needHelpWith.workingWithFirm)
    }
    if (initialProfile?.needHelpWith.supportOrg === "Other") {
      setRequireSupportOrgOther(true)
    }
    // eslint-disable-next-line
  }, [founderProfile]);

  // Error-clearing effects
  useEffect(() => {
    if (errorArray.includes("govtSupport")) {
      setErrorArray(prev => prev.filter(item => item !== "govtSupport"));
    }
  }, [govtSupport])

  useEffect(() => {
    if (errorArray.includes("seekingEducation")) {
      setErrorArray(prev => prev.filter(item => item !== "seekingEducation"));
    }
  }, [seekingEducation])

  useEffect(() => {
    if (errorArray.includes("businessGoal")) {
      setErrorArray(prev => prev.filter(item => item !== "businessGoal"));
    }
  }, [businessGoal])

  useEffect(() => {
    if (errorArray.includes("agreementSigned")) {
      setErrorArray(prev => prev.filter(item => item !== "agreementSigned"));
    }
  }, [agreementSigned])

  useEffect(() => {
    if (errorArray.includes("workingWithFirm")) {
      setErrorArray(prev => prev.filter(item => item !== "workingWithFirm"));
    }
    if (workingWithFirm === false && errorArray.includes("firmId")) {
      setErrorArray(prev => prev.filter(item => item !== "firmId"));
    }
    if (workingWithFirm === false && errorArray.includes("firmOther")) {
      setErrorArray(prev => prev.filter(item => item !== "firmOther"));
    }
  }, [workingWithFirm])

  useEffect(() => {
    if (errorArray.includes("patentsReadyOptions")) {
      if (patentsReadyOptions.length > 0) {
        setErrorArray(prev => prev.filter(item => item !== "patentsReadyOptions"));
      }
    }
  }, [patentsReadyOptions])

  // Scroll to first error
  useEffect(() => {
    const firstError = document.querySelector('.reqFieldError');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submittedSectionError])

  const handleNeedHelpWithChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const { id, value } = event.target;
    handleNeedHelpWithSelectChange(id, value);
  };

  const handleNeedHelpWithSelectChange = (id: string, value: string) => {
    if (id === "firmId" && value === "Other") {
      setRequireFirmOther(true)

    }
    if (id === "firmId" && value !== "Other") {
      setRequireFirmOther(false)
      if (errorArray.includes("firmOther")) {
        setErrorArray(prev => prev.filter(item => item !== "firmOther"));
      }
    }
    if (id === "supportOrg" && value === "Other") {
      setRequireSupportOrgOther(true)
    }
    if (id === "supportOrg" && value !== "Other") {
      setRequireSupportOrgOther(false)
      if (errorArray.includes("supportOrgOther")) {
        setErrorArray(prev => prev.filter(item => item !== "supportOrgOther"));
      }
    }
    if (errorArray.includes(id)) {
      setErrorArray(prev => prev.filter(item => item !== id));
    }
    setNeedHelpWithStateObj(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleTopicsCheckboxChange = (value: string) => {
    if (errorArray.includes("topics")) {
      setErrorArray(prev => prev.filter(item => item !== "topics"));
    }
    setTopics(prev => {
      const updated = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];

      return updated;
    });
  };

  const handleDesiredActivitesCheckboxChange = (value: string) => {
    if (errorArray.includes("desiredActivities")) {
      setErrorArray(prev => prev.filter(item => item !== "desiredActivities"));
    }
    setDesiredActivities(prev => {
      const updated = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];

      // Check for "Other" after update
      setRequireDesiredActivitesOther(updated.includes("Other"));
      // Clear conditional errors when their trigger is deselected
      if (errorArray.includes("searchCompleted") && !updated.includes("Trademark Filing")) {
        setErrorArray(prev => prev.filter(item => item !== "searchCompleted"));
      }
      if (errorArray.includes("exclusiveRights") && updated.length === 0) {
        setErrorArray(prev => prev.filter(item => item !== "exclusiveRights"));
      }
      return updated;
    });
  };

  const handlePatentsReadyOptionsCheckboxChange = (value: string) => {
    setPatentsReadyOptions(prev => {
      const updated = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];
      return updated;
    });
  }

  const needHelpWithErrorCheck = () => {
    let i = 0
    if (seekingEducation === null) {
      setErrorArray(prev => [...prev, "seekingEducation"])
      i++
    }
    if (agreementSigned === null) {
      setErrorArray(prev => [...prev, "agreementSigned"])
      i++
    }
    if (workingWithFirm === null) {
      setErrorArray(prev => [...prev, "workingWithFirm"])
      i++
    }
    if (businessGoal === null) {
      setErrorArray(prev => [...prev, "businessGoal"])
      i++
    }
    if (businessGoal && !needHelpWithStateObj.specificDiscussion) {
      setErrorArray(prev => [...prev, "specificDiscussion"])
      i++
    }
    if (businessGoal && topics.length === 0) {
      setErrorArray(prev => [...prev, "topics"])
      i++
    }
    if (requireDesiredActivitesOther && !needHelpWithStateObj.activitiesOther) {
      setErrorArray(prev => [...prev, "activitiesOther"])
      i++
    }

    if (desiredActivities.length > 0 && !needHelpWithStateObj.exclusiveRights && !founderProfile?.needHelpWith?.exclusiveRights) {
      setErrorArray(prev => [...prev, "exclusiveRights"])
      i++
    }

    if (desiredActivities.includes("Trademark Filing")  && !needHelpWithStateObj.searchCompleted) {
      setErrorArray(prev => [...prev, "searchCompleted"])
      i++
    }
    if (desiredActivities.includes("Patent Filing") && patentsReadyOptions.length === 0) {
      setErrorArray(prev => [...prev, "patentsReadyOptions"])
      i++
    }
    if (workingWithFirm === null) {
      setErrorArray(prev => [...prev, "workingWithFirm"])
      i++
    }
    if (workingWithFirm && !needHelpWithStateObj.firmId) {
      setErrorArray(prev => [...prev, "firmId"])
      i++
    }
    if (requireFirmOther && !needHelpWithStateObj.firmOther) {
      setErrorArray(prev => [...prev, "firmOther"])
      i++
    }
    if (govtSupport === null) {
      setErrorArray(prev => [...prev, "govtSupport"])
      i++
    }
    if (govtSupport && !needHelpWithStateObj.supportOrg) {
      setErrorArray(prev => [...prev, "supportOrg"])
      i++
    }
    if (requireSupportOrgOther && !needHelpWithStateObj.supportOrgOther) {
      setErrorArray(prev => [...prev, "supportOrgOther"])
      i++
    }

    if (i === 0) {
      // Assemble needHelpWith and pass to parent
      const assembledNeedHelpWith: T.NeedHelpWith = {
        seekingEducation: seekingEducation as boolean,
        businessGoal: businessGoal as boolean,
        topics: topics,
        agreementSigned: agreementSigned as string,
        desiredActivities: desiredActivities,
        patentsReadyOptions: patentsReadyOptions,
        workingWithFirm: workingWithFirm as boolean,
        firmId: needHelpWithStateObj.firmId as string,
        govtSupport: govtSupport as boolean,
      }
      if (needHelpWithStateObj.specificDiscussion) {
        assembledNeedHelpWith.specificDiscussion = needHelpWithStateObj.specificDiscussion as string
      }
      if (requireDesiredActivitesOther && needHelpWithStateObj.activitiesOther) {
        assembledNeedHelpWith.activitiesOther = needHelpWithStateObj.activitiesOther as string
      }
      if (needHelpWithStateObj.searchCompleted) {
        assembledNeedHelpWith.searchCompleted = needHelpWithStateObj.searchCompleted as string
      }
      if (needHelpWithStateObj.exclusiveRights) {
        assembledNeedHelpWith.exclusiveRights = needHelpWithStateObj.exclusiveRights as string
      }
      if (needHelpWithStateObj.ipOwnerName) {
        assembledNeedHelpWith.ipOwnerName = needHelpWithStateObj.ipOwnerName as string
      }
      if (requireFirmOther && needHelpWithStateObj.firmOther) {
        assembledNeedHelpWith.firmOther = needHelpWithStateObj.firmOther as string
      }
      if (govtSupport && needHelpWithStateObj.supportOrg) {
        assembledNeedHelpWith.supportOrg = needHelpWithStateObj.supportOrg as string
      }
      if (requireSupportOrgOther && needHelpWithStateObj.supportOrgOther) {
        assembledNeedHelpWith.supportOrgOther = needHelpWithStateObj.supportOrgOther as string
      }
      onSave(assembledNeedHelpWith)
    } else {
      setSubmittedSectionError(!submittedSectionError)
      onValidationFail()
    }
  }

  // Expose save trigger to parent for sticky header
  if (onSaveRef) onSaveRef.current = () => needHelpWithErrorCheck();

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <div className="flex flex-col mb-6 w-full">
          <p className="mb-2">{langState === "fr" ? "Pour le moment, concernant cette demande, je recherche une formation générale en propriété intellectuelle (PI) (p. ex., par où commencer? Qu'est-ce qu'un brevet? etc.)" : "At this time, for this application, I'm seeking General IP Education (e.g. where do I start? What is a patent?, etc.)"}</p>
          <div className="mb-2">
          {errorArray.includes("seekingEducation") &&
            requiredFieldErrorMessage(langState)
          }
          </div>
          <div className="flex gap-4">
            <RadioGroup className="flex gap-4" value={seekingEducation === null ? undefined : seekingEducation ? "yes" : "no"} onValueChange={(v) => setSeekingEducation(v === "yes")}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="seekingEducation-yes" />
                <Label htmlFor="seekingEducation-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="seekingEducation-no" />
                <Label htmlFor="seekingEducation-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>
      <div className="flex flex-col mb-6 w-full">
        <div>
          <p className="mb-2">{langState === "fr" ? "Pour cette demande, j'aimerais rencontrer un conseiller en PI pour obtenir des conseils sur la façon d'aligner ma stratégie de PI avec ma stratégie et mes objectifs d'affaires." : "At this time, for this application, I'd like to meet with an IP Advisor for coaching on how to align my IP strategy with my business strategy/goals"}</p>
          <div className="mb-2">
          {errorArray.includes("businessGoal") &&
            requiredFieldErrorMessage(langState)
          }
          </div>
        </div>
        <div className="flex gap-4">
          <RadioGroup className="flex gap-4" value={businessGoal === null ? undefined : businessGoal ? "yes" : "no"} onValueChange={(v) => setBusinessGoal(v === "yes")}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="businessGoal-yes" />
              <Label htmlFor="businessGoal-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="businessGoal-no" />
              <Label htmlFor="businessGoal-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      {businessGoal &&
      <div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2 w-full">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="topics">{langState === "fr" ? "Veuillez sélectionner les sujets que vous souhaitez aborder avec notre conseiller en PI." : "Please select the topics that you'd like to discuss with our IP Advisor:"}</label>
              {errorArray.includes("topics") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="flex flex-wrap" id="topics">
            {topicsOptions.map((item) => (
              <div className="w-1/2" key={item.value}>
                <div className="flex items-center gap-2 my-1.5">
                  <Checkbox id={`topic-${item.value}`} checked={topics.includes(item.value)} onCheckedChange={() => handleTopicsCheckboxChange(item.value)} />
                  <Label htmlFor={`topic-${item.value}`} className="cursor-pointer font-normal">{langState === "fr" && item.frText ? item.frText : item.text}</Label>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="specificDiscussion">{langState === "fr" ? "Sans faire explicitement référence à des informations sensibles relatives à la propriété intellectuelle, veuillez fournir un aperçu général de ce que vous souhaitez aborder avec notre conseiller en PI." : "Without specifically referencing IP sensitive information, please provide a general overview of what you' like to discuss with our IP Advisor"}</label>
              {errorArray.includes("specificDiscussion") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Textarea placeholder="" id="specificDiscussion" name="specificDiscussion"
            className="w-full min-h-[100px]"
            value={needHelpWithStateObj.specificDiscussion} onChange={handleNeedHelpWithChange} />
          </div>
        </div>
      </div>
    }
      <div className="flex flex-col mb-6 w-full">
        <div>
          <p className="mb-2">{langState === "fr" ? "Avez-vous, vous et vos cofondateurs, signé un « accord de cession de PI des cofondateurs » qui attribue la PI à la société?" : "Have you and your cofounders signed a \"Co-founders IP assignment agreement\" that assigns IP to the corporation?"}</p>
          <div className="mb-2">
          {errorArray.includes("agreementSigned") &&
            requiredFieldErrorMessage(langState)
          }
          </div>
        </div>
        <div className="flex gap-4">
          <RadioGroup className="flex gap-4" value={agreementSigned ?? undefined} onValueChange={(v) => setAgreementSigned(v)}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="agreementSigned-yes" />
              <Label htmlFor="agreementSigned-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="agreementSigned-no" />
              <Label htmlFor="agreementSigned-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="noCofounder" id="agreementSigned-noCofounder" />
              <Label htmlFor="agreementSigned-noCofounder" className="cursor-pointer font-normal">{langState === "fr" ? "Pas de cofondateur" : "No Co-founder"}</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2 w-full">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="desiredActivities">{langState === "fr" ? "Pour cette demande, j'aimerais de l'aide pour couvrir les coûts d'un fournisseur de services en PI pour les activités suivantes :" : "At this time, for this application, I'd like help covering IP Service Provider costs for the following activities:"}</label>
              {errorArray.includes("desiredActivities") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="flex flex-wrap" id="desiredActivities">
            {desiredActivitiesOptions.map((item) => (
              <div className="w-1/2" key={item.value}>
                <div className="flex items-center gap-2 my-1.5">
                  <Checkbox id={`da-${item.value}`} checked={desiredActivities.includes(item.value)} onCheckedChange={() => handleDesiredActivitesCheckboxChange(item.value)} />
                  <Label htmlFor={`da-${item.value}`} className="cursor-pointer font-normal">{langState === "fr" && item.frText ? item.frText : item.text}</Label>
                </div>
              </div>
            ))}
          </div>
        </div>
        {
        requireDesiredActivitesOther &&
        <div className="flex flex-col mb-6 w-full">
        <div className="flex mb-2">
          <div className="flex flex-col">
            <label className="text-base font-medium text-text" htmlFor="activitiesOther">{langState === "fr" ? "Veuillez préciser" : "Please specify"}</label>
            {errorArray.includes("activitiesOther") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
        </div>
        <div className="relative flex items-center">
          <Input type="text" id="activitiesOther" name="activitiesOther"
          className="w-full"
          value={needHelpWithStateObj.activitiesOther} onChange={handleNeedHelpWithChange} />
        </div>
      </div>
      }
    {desiredActivities.includes("Trademark Filing") &&
    <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2">
        <div className="flex">
          <label className="text-base font-medium text-text" htmlFor="searchCompleted">{langState === "fr" ? "Si vous pensez être prêt à enregistrer des marques dans les 3 à 6 prochains mois, votre fournisseur de services en PI a-t-il effectué une recherche de disponibilité?" : "If you think you are ready to register trademarks in the next 3-6 months, has your IP service provider completed a clearance search?"}</label>
          {errorArray.includes("searchCompleted") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="relative flex items-center">
        <Select value={needHelpWithStateObj.searchCompleted || undefined} onValueChange={(v) => handleNeedHelpWithSelectChange("searchCompleted", v)}>
          <SelectTrigger className="w-full border-border">
            <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">
              {langState === "fr" ? "Oui" : "Yes"}
            </SelectItem>
            <SelectItem value="no">
              {langState === "fr" ? "Non" : "No"}
            </SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>
    }
    {desiredActivities.length > 0 &&
    <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2">
        <div className="flex">
          <label className="text-base font-medium text-text" htmlFor="exclusiveRights">{langState === "fr" ? "Si la propriété de votre PI est partagée (p. ex. avec un établissement universitaire), avez-vous les droits exclusifs de commercialiser la technologie?" : "If the ownership of your IP is shared (e.g. with an academic institution), do you have exclusive rights to commercialize the technology?"}</label>
          {errorArray.includes("exclusiveRights") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="relative flex items-center">
        <Select value={needHelpWithStateObj.exclusiveRights || undefined} onValueChange={(v) => handleNeedHelpWithSelectChange("exclusiveRights", v)}>
          <SelectTrigger className="w-full border-border">
            <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">
              {langState === "fr" ? "Oui" : "Yes"}
            </SelectItem>
            <SelectItem value="no">
              {langState === "fr" ? "Non" : "No"}
            </SelectItem>
            <SelectItem value="n/a">
              {langState === "fr" ? "S.O." : "N/A"}
            </SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>
    }
    {desiredActivities.length > 0 && needHelpWithStateObj.exclusiveRights === "no" &&
    <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2">
        <div className="flex flex-col">
          <label className="text-base font-medium text-text" htmlFor="ipOwnerName">{langState === "fr" ? "Qui est le propriétaire?" : "Who is the owner?"}</label>
          {errorArray.includes("ipOwnerName") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="relative flex items-center">
        <Input type="text" id="ipOwnerName" name="ipOwnerName"
        className="w-full"
        value={needHelpWithStateObj.ipOwnerName} onChange={handleNeedHelpWithChange} />
      </div>
    </div>
    }
    {desiredActivities.includes("Patent Filing") &&
      <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2 w-full">
        <div className="flex">
          <label className="text-base font-medium text-text" htmlFor="patentsReadyOptions">{langState === "fr" ? "Si vous pensez être prêt à déposer des brevets dans les 3 à 6 prochains mois, veuillez indiquer lesquels des énoncés suivants s'appliquent à la technologie que vous souhaitez breveter :" : "If you think you are ready to file patents in the next 3-6 months, please indicate which of the following apply to the technology you intend to patent:"}</label>
          {errorArray.includes("patentsReadyOptions") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="flex flex-wrap" id="patentsReadyOptions">
        {patentsReadyOptionsOptions.map((item) => (
          <div className="w-1/2" key={item.value}>
            <div className="flex items-center gap-2 my-1.5">
              <Checkbox id={`pro-${item.value}`} checked={patentsReadyOptions.includes(item.value)} onCheckedChange={() => handlePatentsReadyOptionsCheckboxChange(item.value)} />
              <Label htmlFor={`pro-${item.value}`} className="cursor-pointer font-normal">{langState === "fr" && item.frText ? item.frText : item.text}</Label>
            </div>
          </div>
        ))}
      </div>
    </div>
  }
    <div className="flex flex-col mb-6 w-full">
      <p className="mb-2">{langState === "fr" ? "Votre entreprise travaille-t-elle actuellement avec le fournisseur de services en PI que vous comptez utiliser pour le soutien sollicité dans le cadre du programme ÉleverlaPI?" : "Is your company currently working with an IP Service Provider that you intend to use for the support being sought from the Elevate IP program?"}</p>
      <div className="mb-2">
        {errorArray.includes("workingWithFirm") &&
          requiredFieldErrorMessage(langState)
        }
      </div>
      <div className="flex gap-4">
        <RadioGroup className="flex gap-4" value={workingWithFirm === null ? undefined : workingWithFirm ? "yes" : "no"} onValueChange={(v) => setWorkingWithFirm(v === "yes")}>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id="workingWithFirm-yes" />
            <Label htmlFor="workingWithFirm-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="workingWithFirm-no" />
            <Label htmlFor="workingWithFirm-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
    {workingWithFirm &&
    <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2">
        <div className="flex">
          <label className="text-base font-medium text-text" htmlFor="firmId">{langState === "fr" ? "Veuillez sélectionner l'entreprise (sélectionnez « Autre » si votre entreprise actuelle ne figure pas dans la liste)." : "Please select the firm (select \"Other\" if your current firm is not listed)"}</label>
          {errorArray.includes("firmId") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="relative flex items-center">
        <Select value={needHelpWithStateObj.firmId || undefined} onValueChange={(v) => handleNeedHelpWithSelectChange("firmId", v)}>
          <SelectTrigger className="w-full border-border">
            <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
          </SelectTrigger>
          <SelectContent>
            {regionalFirmOptions
              .slice()
              .sort((a, b) => {
                const textA = langState === "fr" ? a.frText : a.text;
                const textB = langState === "fr" ? b.frText : b.text;
                return textA.localeCompare(textB);
              })
              .map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {langState === "fr" ? item.frText : item.text}
                </SelectItem>
            ))}
            <SelectItem value="Other">{langState === "fr" ? "Autre" : "Other" }</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    }
    {
    workingWithFirm && requireFirmOther &&
      <div className="flex flex-col mb-6 w-full">
        <div className="flex mb-2">
          <div className="flex flex-col">
            <label className="text-base font-medium text-text" htmlFor="firmOther">{langState === "fr" ? "Veuillez préciser" : "Please specify"}</label>
            {errorArray.includes("firmOther") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
        </div>
        <div className="relative flex items-center">
          <Input type="text" id="firmOther" name="firmOther"
          className="w-full"
          value={needHelpWithStateObj.firmOther} onChange={handleNeedHelpWithChange} />
        </div>
      </div>
    }
    <div className="flex flex-col mb-6 w-full">
      <p className="mb-2">{langState === "fr" ? "Votre entreprise a-t-elle déjà bénéficié d'un soutien financier gouvernemental lié à la propriété intellectuelle?" : "Has your company ever received government funded support related to Intellectual Property?"}</p>
      <div className="mb-2">
      {errorArray.includes("govtSupport") &&
        requiredFieldErrorMessage(langState)
      }
      </div>
      <div className="flex gap-4">
        <RadioGroup className="flex gap-4" value={govtSupport === null ? undefined : govtSupport ? "yes" : "no"} onValueChange={(v) => setGovtSupport(v === "yes")}>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id="govtSupport-yes" />
            <Label htmlFor="govtSupport-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="govtSupport-no" />
            <Label htmlFor="govtSupport-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
    {
      govtSupport &&
    <div className="flex flex-col mb-6 w-full">
      <div className="flex mb-2">
        <div className="flex">
          <label className="text-base font-medium text-text" htmlFor="supportOrg">{langState === "fr" ? "Veuillez préciser quelle organisation:" : "Please select which organization:"}</label>
          {errorArray.includes("supportOrg") &&
            requiredFieldErrorMessage(langState)
          }
        </div>
      </div>
      <div className="relative flex items-center">
        <Select value={needHelpWithStateObj.supportOrg || undefined} onValueChange={(v) => handleNeedHelpWithSelectChange("supportOrg", v)}>
          <SelectTrigger className="w-full border-border">
            <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Intellectual Property Ontario (IPON)">
              {langState === "fr" ? "Propriété intellectuelle Ontario (IPON)" : "Intellectual Property Ontario (IPON)"}
            </SelectItem>
            <SelectItem value="NRC IP Assist">
              {langState === "fr" ? "Aide en PI du CNRC" : "NRC IP Assist"}
            </SelectItem>
            <SelectItem value="IAC">
              IAC
            </SelectItem>
            <SelectItem value="ElevateIP">
              {langState === "fr" ? "ÉleverlaPI" : "ElevateIP"}
            </SelectItem>
            <SelectItem value="Other">
              {langState === "fr" ? "Autre" : "Other"}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    }
    {
      requireSupportOrgOther &&
      <div className="flex flex-col mb-6 w-full">
        <div className="flex mb-2">
          <div className="flex flex-col">
            <label className="text-base font-medium text-text" htmlFor="supportOrgOther">{langState === "fr" ? "Veuillez préciser" : "Please specify"}</label>
            {errorArray.includes("supportOrgOther") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
        </div>
        <div className="relative flex items-center">
          <Input type="text" id="supportOrgOther" name="supportOrgOther"
          className="w-full"
          value={needHelpWithStateObj.supportOrgOther} onChange={handleNeedHelpWithChange} />
        </div>
      </div>
    }
  </div>
  )
}

export default NeedHelpWithSection;
